import React, { FC, useMemo } from 'react';

// components
import { SearchableSelectType } from '../../../../../components/inputs/searchableSelect';

import useGetComponentTypeIdByVariant from '../../../../../hooks/component-types/useGetComponentTypeIdByVariant';
import useLabelCreateMutation from '../../../../../hooks/labels/useLabelCreateMutation';
import useLabelsQuery from '../../../../../hooks/labels/useLabelsQuery';

// models
import { ImageAlternateModalId } from '../../../../../models/alternate.model';
import { VariantType } from '../../../../../models/variant.model';

import LabelsCreateAndSelectDropdown from '../../../Sidebar/LabelsCreateAndSelectDropdown';

interface ImageAlternateLabelDropdownProps {
    selected?: number;
    formulaId: number;
    handleUpdateLabel: (labelId?: number) => void;
    isDisabled?: boolean;
}

const ImageAlternateLabelDropdown: FC<
    React.PropsWithChildren<ImageAlternateLabelDropdownProps>
> = ({ selected, formulaId, handleUpdateLabel, isDisabled = false }) => {
    const imageComponentTypeId = useGetComponentTypeIdByVariant(
        VariantType.Image,
    );

    const { data: labels } = useLabelsQuery(imageComponentTypeId as string);

    const createLabelMutation = useLabelCreateMutation(
        VariantType.Image,
        formulaId,
    );

    const labelOptions = useMemo<SearchableSelectType[]>(
        () =>
            labels?.map((label) => ({
                label: label.label_name,
                value: label.id,
            })) || [],
        [labels],
    );

    const selectedOption = useMemo<SearchableSelectType | undefined>(
        () => labelOptions.find((item) => item.value === selected),
        [labelOptions, selected],
    );

    const createLabelAndAttachToAlternate = (
        selectedLabel: SearchableSelectType,
    ) => {
        createLabelMutation.mutate(
            {
                label_name: selectedLabel.label,
                variant_type: VariantType.Image,
            },
            {
                onSuccess: ({ data }) => {
                    handleUpdateLabel(data.id);
                },
                onSettled: () => {
                    setTimeout(createLabelMutation.reset, 3000);
                },
            },
        );
    };

    const handleLabelInputChange = (selectedLabel: SearchableSelectType) => {
        if (!selectedLabel.value) {
            handleUpdateLabel(undefined);
            return;
        }

        const isLabelPresent = labels?.find(
            (item) => item.id === selectedLabel.value,
        );

        if (!isLabelPresent) {
            createLabelAndAttachToAlternate(selectedLabel);
            return;
        }

        handleUpdateLabel(selectedLabel.value as number);
    };

    const menuPortal = document.getElementById(ImageAlternateModalId);

    return (
        <LabelsCreateAndSelectDropdown
            name={'label'}
            options={labelOptions}
            value={selectedOption}
            onChange={(field, value) =>
                handleLabelInputChange(value as SearchableSelectType)
            }
            placeholder={
                labelOptions?.length
                    ? 'Select an option or create one'
                    : 'Create a label'
            }
            isCreatable
            isDisabled={isDisabled}
            menuPortalTarget={menuPortal}
        />
    );
};

export default ImageAlternateLabelDropdown;
